/**
 * Session-replay recorder engine.
 *
 * rrweb captures the DOM (input VALUES masked by default; block/mask regions via
 * the marker classes). Events buffer in memory and are gzipped + POSTed to the
 * ingest endpoint every few seconds (and on tab-hide). Notable moments (start,
 * nav, error, toast, rageclick) are emitted both into the rrweb stream (so they
 * replay in sync) and into a small `index` the backend stores for the timeline.
 *
 * Nothing here may throw into the app — every hook is defensively wrapped.
 */

import type * as RRWeb from "rrweb";
import { gzipToBase64, supportsCompression } from "./gzip";
import { startNewReplay, clearReplayState, type ReplayState } from "./id";
import {
  INGEST_URL,
  BATCH_MS,
  CHECKOUT_MS,
  MAX_REPLAY_MS,
  RAGE_CLICKS,
  RAGE_WINDOW_MS,
  RAGE_RADIUS_PX,
  BLOCK_CLASS,
  MASK_TEXT_CLASS,
  IGNORE_CLASS,
} from "./config";

export type ReplayEventKind =
  | "start"
  | "nav"
  | "error"
  | "toast"
  | "fetch"
  | "rageclick";

interface IndexEvent {
  offsetMs: number;
  kind: ReplayEventKind;
  summary?: string;
  url?: string;
  traceId?: string;
  errorSignature?: string;
}

interface StartOptions {
  userId: string;
  getToken: () => string | undefined;
}

interface Session {
  state: ReplayState;
  stopRecord?: () => void;
  buffer: unknown[];
  index: IndexEvent[];
  seq: number;
  entryUrl: string;
  flushTimer: number | null;
  rotateTimer: number | null;
  teardown: Array<() => void>;
  flushing: boolean;
  getToken: () => string | undefined;
}

let session: Session | null = null;

// rrweb's `record` is loaded lazily (dynamic import in startReplay) so the
// library stays out of the shared client bundle — it's fetched only when a
// replay session actually starts. Null until the first startReplay() loads it.
let record: typeof RRWeb.record | null = null;

export function isReplayActive(): boolean {
  return session !== null;
}

function safePath(): string {
  try {
    return window.location.pathname;
  } catch {
    return "";
  }
}

/** Public: record a timeline moment (also embedded in the rrweb stream). */
export function recordReplayEvent(
  kind: ReplayEventKind,
  payload: Omit<IndexEvent, "offsetMs" | "kind"> = {},
): void {
  const s = session;
  if (!s) return;
  const offsetMs = Math.max(0, Date.now() - s.state.startedAt);
  try {
    (record as unknown as {
      addCustomEvent?: (tag: string, payload: unknown) => void;
    }).addCustomEvent?.(`rawiah:${kind}`, { kind, ...payload });
  } catch {
    /* ignore — the index copy below is what the timeline uses */
  }
  s.index.push({ offsetMs, kind, ...payload });
}

export function startReplay(opts: StartOptions): void {
  if (typeof window === "undefined" || !supportsCompression()) return;
  if (session) {
    if (session.state.userId === opts.userId) return; // already recording this user
    stopReplay(); // identity changed → clean restart
  }

  const state = startNewReplay(opts.userId);
  const s: Session = {
    state,
    buffer: [],
    index: [],
    seq: 0,
    entryUrl: safePath(),
    flushTimer: null,
    rotateTimer: null,
    teardown: [],
    flushing: false,
    getToken: opts.getToken,
  };
  session = s;

  // Load rrweb on demand. This keeps the (large) library out of the shared
  // client bundle — it's fetched only when a session actually starts recording.
  void import("rrweb")
    .then(({ record: rec }) => {
      if (session !== s) return; // stopped or replaced while the chunk loaded
      record = rec;
      try {
        s.stopRecord = rec({
          emit: (event) => {
            s.buffer.push(event);
          },
          maskAllInputs: true,
          blockClass: BLOCK_CLASS,
          maskTextClass: MASK_TEXT_CLASS,
          ignoreClass: IGNORE_CLASS,
          checkoutEveryNms: CHECKOUT_MS,
          recordCanvas: false,
          collectFonts: false,
        }) as (() => void) | undefined;
      } catch {
        if (session === s) session = null;
        return;
      }

      recordReplayEvent("start", { url: s.entryUrl });

      s.flushTimer = window.setInterval(() => void flush(false), BATCH_MS);
      s.rotateTimer = window.setInterval(() => maybeRotate(opts), 30_000);
      wireHooks(s);
    })
    .catch(() => {
      if (session === s) session = null;
    });
}

export function stopReplay(): void {
  const s = session;
  if (!s) return;
  session = null; // stop accepting events first
  try {
    s.stopRecord?.();
  } catch {
    /* ignore */
  }
  if (s.flushTimer) window.clearInterval(s.flushTimer);
  if (s.rotateTimer) window.clearInterval(s.rotateTimer);
  s.teardown.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
}

/** Logout / pre-impersonation: stop AND forget so the next start is unchained. */
export function stopAndClearReplay(): void {
  stopReplay();
  clearReplayState();
}

async function flush(final: boolean): Promise<void> {
  const s = session;
  if (!s || s.flushing) return;
  if (s.buffer.length === 0 && s.index.length === 0) return;
  const token = s.getToken();
  if (!token) return; // between refreshes — keep buffering, try next tick

  s.flushing = true;
  const events = s.buffer;
  s.buffer = [];
  const index = s.index;
  s.index = [];
  const seq = s.seq++;
  const { state, entryUrl } = s;
  const lastOffsetMs = Math.max(0, Date.now() - state.startedAt);

  try {
    const eventsB64 = await gzipToBase64(JSON.stringify(events));
    await fetch(INGEST_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        replayId: state.replayId,
        seq,
        meta: {
          startedAt: new Date(state.startedAt).toISOString(),
          entryUrl,
          userAgent: navigator.userAgent.slice(0, 500),
          tabId: state.tabId,
          continuedFromReplayId: state.continuedFromReplayId,
          lastOffsetMs,
        },
        index,
        events: eventsB64,
      }),
      keepalive: final,
    });
  } catch {
    // Drop this batch on error — rrweb re-checkouts on the next interval, so the
    // stream stays playable rather than growing an unbounded retry queue.
  } finally {
    s.flushing = false;
  }
}

/** Rotate to a fresh, chained replay id once the current one gets too long. */
function maybeRotate(opts: StartOptions): void {
  const s = session;
  if (!s) return;
  if (Date.now() - s.state.startedAt < MAX_REPLAY_MS) return;

  void flush(false); // ship the tail of the old replay (reads current state)
  const next = startNewReplay(opts.userId); // chains from current (same user+tab)
  s.state = next;
  s.seq = 0;
  s.buffer = [];
  s.index = [];
  s.entryUrl = safePath();
  try {
    (record as unknown as {
      takeFullSnapshot?: (isCheckout?: boolean) => void;
    }).takeFullSnapshot?.(true);
  } catch {
    /* ignore */
  }
  recordReplayEvent("start", { url: s.entryUrl });
}

function errorSignature(message: string, filename?: string): string {
  const norm = (message || "").slice(0, 120).replace(/\d+/g, "#").trim();
  const file = filename ? `@${filename.split("/").pop()}` : "";
  return `${norm}${file}`.slice(0, 200);
}

function describeTarget(t: EventTarget | null): string {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return "";
  const id = el.id ? `#${el.id}` : "";
  const cls =
    typeof el.className === "string" && el.className
      ? `.${el.className.split(/\s+/).slice(0, 2).join(".")}`
      : "";
  return `${el.tagName.toLowerCase()}${id}${cls}`.slice(0, 120);
}

function wireHooks(s: Session): void {
  // Navigation — patch history + popstate.
  const emitNav = () => recordReplayEvent("nav", { url: safePath() });
  const origPush = history.pushState;
  const origReplace = history.replaceState;
  history.pushState = function (this: History, ...args: Parameters<History["pushState"]>) {
    const r = origPush.apply(this, args);
    emitNav();
    return r;
  };
  history.replaceState = function (this: History, ...args: Parameters<History["replaceState"]>) {
    const r = origReplace.apply(this, args);
    emitNav();
    return r;
  };
  window.addEventListener("popstate", emitNav);
  s.teardown.push(() => {
    history.pushState = origPush;
    history.replaceState = origReplace;
    window.removeEventListener("popstate", emitNav);
  });

  // Errors.
  const onError = (e: ErrorEvent) =>
    recordReplayEvent("error", {
      summary: (e.message || "Error").slice(0, 300),
      url: safePath(),
      errorSignature: errorSignature(e.message, e.filename),
    });
  const onRejection = (e: PromiseRejectionEvent) => {
    const msg =
      e.reason instanceof Error
        ? e.reason.message
        : String(e.reason ?? "Unhandled rejection");
    recordReplayEvent("error", {
      summary: msg.slice(0, 300),
      url: safePath(),
      errorSignature: errorSignature(msg),
    });
  };
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  s.teardown.push(() => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  });

  // Rage clicks — N clicks within a window in a small area.
  let clicks: number[] = [];
  let lastX = 0;
  let lastY = 0;
  const onClick = (e: MouseEvent) => {
    const t = Date.now();
    if (
      Math.abs(e.clientX - lastX) > RAGE_RADIUS_PX ||
      Math.abs(e.clientY - lastY) > RAGE_RADIUS_PX
    ) {
      clicks = [];
    }
    lastX = e.clientX;
    lastY = e.clientY;
    clicks = clicks.filter((ts) => t - ts < RAGE_WINDOW_MS);
    clicks.push(t);
    if (clicks.length >= RAGE_CLICKS) {
      clicks = [];
      recordReplayEvent("rageclick", {
        url: safePath(),
        summary: describeTarget(e.target),
      });
    }
  };
  window.addEventListener("click", onClick, true);
  s.teardown.push(() => window.removeEventListener("click", onClick, true));

  // Flush on tab-hide (async gzip still runs; keepalive survives a later unload).
  const onHide = () => {
    if (document.visibilityState === "hidden") void flush(true);
  };
  const onPageHide = () => void flush(true);
  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("pagehide", onPageHide);
  s.teardown.push(() => {
    document.removeEventListener("visibilitychange", onHide);
    window.removeEventListener("pagehide", onPageHide);
  });
}
